import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, ShoppingBag, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import StarRow from "./StarRow";

const API = process.env.REACT_APP_BACKEND_URL;

// Homepage rotating strip: "What readers are saying"
// Shows the featured/approved reviews with an auto-rotate every 6s.
const TestimonialsStrip = () => {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    axios
      .get(`${API}/api/testimonials`)
      .then((res) => setItems(res.data.items || []))
      .catch(() => setItems([]));
  }, []);

  useEffect(() => {
    if (items.length < 2) return;
    const timer = setInterval(() => setIdx((i) => (i + 1) % items.length), 6000);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;
  const current = items[idx];

  return (
    <section
      className="py-16 px-4 md:px-8 bg-gradient-to-br from-[#457B9D]/15 via-[#E9C46A]/20 to-[#2A9D8F]/15"
      data-testid="testimonials-strip"
    >
      <div className="max-w-4xl mx-auto text-center">
        <p className="text-[#E63946] font-black uppercase tracking-widest text-sm mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
          <MessageCircle className="inline w-5 h-5 mb-1" /> What readers are saying
        </p>
        <h2 className="text-3xl sm:text-4xl font-black text-[#1D3557] mb-10" style={{ fontFamily: "'Fredoka', sans-serif" }}>
          Real reviews from real farm friends
        </h2>

        <div className="relative min-h-[220px]">
          <AnimatePresence mode="wait">
            <motion.article
              key={current.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.5 }}
              className="tactile-card p-6 md:p-8 bg-white text-left"
              data-testid="testimonial-card"
            >
              <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                <StarRow value={current.rating} size={22} />
                {current.source === "amazon" && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#FF9900] text-[#1D3557] text-xs font-black border-2 border-[#1D3557]" data-testid="amazon-badge">
                    <ShoppingBag className="w-3.5 h-3.5" />
                    Verified on Amazon
                  </span>
                )}
              </div>
              {current.title && (
                <h3 className="font-black text-lg md:text-xl text-[#1D3557] mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  {current.title}
                </h3>
              )}
              <p className="text-[#1D3557] font-medium leading-relaxed line-clamp-4 whitespace-pre-line">
                {current.body}
              </p>
              <div className="mt-4 flex items-center justify-between flex-wrap gap-2 text-sm">
                <span className="font-bold text-[#1D3557]">— {current.reviewer_name}</span>
                {current.location && (
                  <span className="text-[#1D3557]/70 font-medium inline-flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" />
                    {current.location}
                  </span>
                )}
              </div>
            </motion.article>
          </AnimatePresence>
        </div>

        {/* Pagination dots */}
        {items.length > 1 && (
          <div className="mt-6 flex items-center justify-center gap-2" data-testid="testimonial-dots">
            {items.map((t, i) => (
              <button
                key={t.id}
                onClick={() => setIdx(i)}
                aria-label={`Show review ${i + 1} of ${items.length}`}
                className={`h-2 rounded-full transition-all ${i === idx ? "w-8 bg-[#E63946]" : "w-2 bg-[#1D3557]/30 hover:bg-[#1D3557]/50"}`}
              />
            ))}
          </div>
        )}

        <div className="mt-8">
          <Link
            to="/reviews"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="view-all-reviews-btn"
          >
            Read all reviews & leave your own
          </Link>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsStrip;
